import { formatCurrency, formatMoney, formatNumber, getFirstSubAggregationKey, pickNumber } from '../utils/helpers';
import { getEmployeesSalesColumns } from '../utils/constants';

const parseEmployeesSales = (aggs = {}, opts = {}) => {
  const { topN = 10, currency = '' } = opts;

  if (!aggs) {
    return {
      charts: {
        employeesByAmount: { labels: [], values: [] },
        employeesByProfit: { labels: [], values: [] }
      },
      tableRows: [],
      tableRowsRaw: [],
      summary: {
        totalSalesCount: 0,
        totalAmount: 0,
        totalCost: 0,
        totalProfit: 0,
        totalSalesCountStr: '0',
        totalAmountStr: formatMoney(0, currency),
        totalCostStr: formatMoney(0, currency),
        totalProfitStr: formatMoney(0, currency),
        currency
      },
      mainColumns: getEmployeesSalesColumns(),
    };
  }

  const amountBuckets = aggs.employees_by_amount?.buckets || [];
  const countBuckets = aggs.employees_by_count?.buckets || [];

  const nameFromBucket = (b) => {
    if (!b) return { name: '', surname: '', fullName: '', avatar: null };

    const surname = getFirstSubAggregationKey(b, 'surname') || '';
    const name = getFirstSubAggregationKey(b, 'name') || '';
    const avatar = getFirstSubAggregationKey(b, 'employee_image') || null;

    let fullName = '';
    if (name && surname) {
      fullName = `${name} ${surname}`.trim();
    } else if (name) {
      fullName = name;
    } else if (surname) {
      fullName = surname;
    }

    return { name, surname, fullName, avatar, id: b.key };
  };

  const combined = [];
  const extractFromBucket = (b) => {
    const internalId  = String(b.key);
    const employee = nameFromBucket(b);
    const salesCount = pickNumber(b, ['sales_count']) || 0;
    const amount = pickNumber(b, ['amount_sum']) || 0;
    const cost = pickNumber(b, ['cost_sum']) || 0;
    const profit = pickNumber(b, ['profit']) || (amount - cost);
    return { internalId, employee, salesCount, amount, cost, profit };
  };

  amountBuckets.forEach(b => {
    combined.push(extractFromBucket(b));
  });

  countBuckets.forEach(b => {
    const rec = extractFromBucket(b);
    const existing = combined.find(x => x.internalId === rec.internalId);
    if (!existing) {
      combined.push(rec);
    } else {
      if ((rec.salesCount || 0) > (existing.salesCount || 0)) {
        existing.salesCount = rec.salesCount;
      }
      if (!existing.amount && rec.amount) existing.amount = rec.amount;
      if (!existing.cost && rec.cost) existing.cost = rec.cost;
      if (!existing.profit && rec.profit) existing.profit = rec.profit;
    }
  });

  const tableRowsRaw = combined.map(({ internalId, employee, salesCount, amount, cost, profit }) => ({
    employee: employee.fullName,
    salesCount,
    amount: formatMoney(amount, currency),
    cost: formatMoney(cost, currency),
    profit: formatMoney(profit, currency)
  }));

  const tableRows = combined.map(({ employee, salesCount, amount, cost, profit }) => ([
    { type: 'employee', fullName: employee.fullName, avatar: employee.avatar },
    formatNumber(salesCount),
    formatMoney(amount, currency),
    formatMoney(cost, currency),
    formatMoney(profit, currency),
  ]));

  const sortedByAmount = combined
    .slice()
    .sort((a, b) => (b.amount || 0) - (a.amount || 0))
    .slice(0, topN);

  const sortedByCount = combined
    .slice()
    .sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0))
    .slice(0, topN);

  const charts = {
    employeesByAmount: {
      labels: sortedByAmount.map(r => r.employee || ''),
      values: sortedByAmount.map(r => r.amount || 0),
    },
    employeesByCount: {
      labels: sortedByCount.map(r => r.employee || ''),
      values: sortedByCount.map(r => r.salesCount || 0),
    }
  };

  const totalSalesCount = pickNumber(aggs.total_sales_count || {}, ['value']) ||
    tableRowsRaw.reduce((s, r) => s + (r.salesCount || 0), 0);
  const totalAmount = pickNumber(aggs.total_amount || {}, ['value']) ||
    tableRowsRaw.reduce((s, r) => s + (r.amount || 0), 0);
  const totalCost = pickNumber(aggs.total_cost || {}, ['value']) ||
    tableRowsRaw.reduce((s, r) => s + (r.cost || 0), 0);
  const totalProfit = pickNumber(aggs.total_profit || {}, ['value']) || (totalAmount - totalCost);

  const summary = {
    totalSalesCount,
    totalAmount,
    totalCost,
    totalProfit,
    totalSalesCountStr: formatNumber(totalSalesCount),
    totalAmountStr: formatCurrency(totalAmount, currency),
    totalCostStr: formatCurrency(totalCost, currency),
    totalProfitStr: formatCurrency(totalProfit, currency),
    currency
  };

  const mainColumns = getEmployeesSalesColumns();

  return { charts, tableRows, tableRowsRaw, summary, mainColumns };
}

export default parseEmployeesSales;